import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { collectPlanningState, applyPlanningState } from '../services/studySync';
import ctSliceCache from '../services/ctSliceCache';
import sagittalMriCache from '../services/sagittalMriCache';
import axialMriCache from '../services/axialMriCache';
import API_URL from '../config/api';

const MODALITY_LABELS = { ct: 'CT', mri: 'MRI', xray: 'X-ray' };

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// FastAPI errors are {detail: string}
const readError = async (response) => {
  try {
    const data = await response.json();
    if (typeof data?.detail === 'string') return data.detail;
  } catch (e) {
    // non-JSON body, fall through
  }
  return `Request failed (${response.status})`;
};

function HistoryPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';

  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [confirm, setConfirm] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const messageTimer = useRef(null);

  useEffect(() => () => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
  }, []);

  const showSuccess = (text) => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    setMessage({ type: 'success', text });
    messageTimer.current = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const showError = (text) => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    setMessage({ type: 'error', text });
  };

  const apiGet = async (path) => {
    const response = await fetch(`${API_URL}${path}`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  const apiSend = async (path, { method = 'POST', body = null } = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        ...getAuthHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) throw new Error(await readError(response));
    return response.json();
  };

  const loadStudies = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/studies');
      setStudies(data.studies || []);
    } catch (error) {
      showError(error.message || 'Failed to load scan history');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isViewer) loadStudies();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveCurrentScan = async () => {
    const name = saveName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const result = await apiSend('/studies', {
        body: {
          name,
          description: saveDescription.trim() || null,
          interventions: collectPlanningState()
        }
      });
      setSaveName('');
      setSaveDescription('');
      showSuccess(result.message || 'Scan saved');
      await loadStudies();
    } catch (error) {
      showError(error.message || 'Failed to save scan');
    }
    setBusy(false);
  };

  const openStudy = (study) => setConfirm({
    title: 'Open Saved Scan',
    body: `Opening "${study.name}" replaces the measurements and planning state currently in the workspace. Save the current scan first if you want to keep it.`,
    confirmLabel: 'Open Scan',
    danger: false,
    action: async () => {
      setBusy(true);
      try {
        const result = await apiSend(`/studies/${study.id}/restore`);
        applyPlanningState(result.interventions);
        // The live slot's images just changed — drop client-side slice/volume
        // caches so the viewers reload the restored scan, not the old one.
        try {
          ctSliceCache.clear();
          sagittalMriCache.clear();
          axialMriCache.clear();
        } catch (e) {
          console.warn('Could not clear viewer caches after restore:', e);
        }
        showSuccess(result.message);
        // NOT fromUploadPage: that flag means "fresh processing run" and makes
        // the measurements page discard state and re-run the whole pipeline.
        // A restore just loads the persisted measurements from disk.
        navigate('/measurements', { state: { fromUploadPage: false } });
      } catch (error) {
        showError(error.message || 'Failed to open scan');
        setBusy(false);
      }
    }
  });

  const deleteStudy = (study) => setConfirm({
    title: 'Delete Saved Scan',
    body: `Delete "${study.name}"? This permanently removes the saved measurements and interventions and cannot be undone.`,
    confirmLabel: 'Delete',
    danger: true,
    action: async () => {
      setBusy(true);
      try {
        const result = await apiSend(`/studies/${study.id}`, { method: 'DELETE' });
        showSuccess(result.message);
        await loadStudies();
      } catch (error) {
        showError(error.message || 'Failed to delete scan');
      }
      setBusy(false);
    }
  });

  if (isViewer) {
    return (
      <div className="page">
        <div className="card">
          <h2 className="section-title">Scan History</h2>
          <div className="empty">
            <div className="empty__title">Restricted area</div>
            <div className="empty__sub">
              Scan history requires technician access or above.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="row row--between" style={styles.headerRow}>
        <h1 className="section-title" style={styles.pageTitle}>Scan History</h1>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={loadStudies}
          disabled={loading || busy}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {message.text && (
        <div
          className={`alert ${message.type === 'error' ? 'alert--danger' : 'alert--success'}`}
          style={styles.messageRow}
        >
          <span style={styles.messageText}>{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage({ type: '', text: '' })}
            style={styles.closeBtn}
            aria-label="Dismiss message"
          >
            ×
          </button>
        </div>
      )}

      {/* Save current scan */}
      <div className="card">
        <h2 className="section-title">Save Current Scan</h2>
        <p className="section-sub" style={styles.sectionSub}>
          Store the workspace's measurements, alignment parameters, and
          intervention selections so this case can be reopened later —
          even after a new scan is run.
        </p>
        <div style={styles.saveRow}>
          <input
            className="input"
            style={{ flex: '1 1 200px', minWidth: 0 }}
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Case name (e.g. Patient 114 — lumbar CT)"
            maxLength={200}
          />
          <input
            className="input"
            style={{ flex: '2 1 260px', minWidth: 0 }}
            value={saveDescription}
            onChange={(e) => setSaveDescription(e.target.value)}
            placeholder="Notes (optional)"
            maxLength={1000}
          />
          <button
            type="button"
            className="btn btn--primary"
            style={{ flex: '0 1 auto' }}
            onClick={saveCurrentScan}
            disabled={busy || !saveName.trim()}
          >
            {busy ? 'Working…' : 'Save Scan'}
          </button>
        </div>
      </div>

      {/* Saved studies */}
      <div className="card" style={styles.listCard}>
        <h2 className="section-title">
          Completed Scans{studies.length > 0 ? ` (${studies.length})` : ''}
        </h2>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div className="spinner"></div>
            <span style={styles.loadingText}>Loading scan history…</span>
          </div>
        ) : studies.length === 0 ? (
          <div className="empty">
            <div className="empty__title">No saved scans yet</div>
            <div className="empty__sub">Run a scan, then save it above.</div>
          </div>
        ) : (
          <div className="stack" style={styles.studyList}>
            {studies.map((study, index) => {
              const canDelete = isAdmin || study.created_by_username === user?.username;
              return (
                <div
                  key={study.id}
                  className="panel rise-in"
                  style={{ ...styles.studyCard, '--i': Math.min(index, 12) }}
                >
                  <div style={styles.studyInfo}>
                    <div style={styles.studyName}>
                      <span style={styles.studyNameText}>{study.name}</span>
                      <span style={styles.modalityChips}>
                        {study.modalities.map(m => (
                          <span key={m} className="badge badge--accent">
                            {MODALITY_LABELS[m] || m.toUpperCase()}
                          </span>
                        ))}
                        {study.has_interventions && (
                          <span className="badge badge--success">Plan</span>
                        )}
                      </span>
                    </div>
                    {study.description && (
                      <div style={styles.studyDescription}>{study.description}</div>
                    )}
                    <div style={styles.studyMeta}>
                      Saved {formatDateTime(study.created_at)}
                      {study.created_by_username ? ` by ${study.created_by_username}` : ''}
                      {study.stl_count > 0 ? ` · ${study.stl_count} 3D models` : ''}
                    </div>
                    <div style={styles.studyContents}>
                      {study.contents.join(' · ')}
                    </div>
                  </div>
                  <div style={styles.studyActions}>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => openStudy(study)}
                      disabled={busy}
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      onClick={() => deleteStudy(study)}
                      disabled={busy || !canDelete}
                      title={canDelete ? 'Delete this saved scan' : 'Only the creator or an admin can delete'}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div className="modal-scrim">
          <div className="modal" style={styles.confirmModal}>
            <div className="modal__header">
              <h3 className="modal__title">{confirm.title}</h3>
            </div>
            <div className="modal__body">
              <p style={styles.confirmBody}>{confirm.body}</p>
            </div>
            <div className="modal__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirm(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${confirm.danger ? 'btn--danger-solid' : 'btn--primary'}`}
                onClick={() => {
                  const action = confirm.action;
                  setConfirm(null);
                  action();
                }}
                disabled={busy}
              >
                {confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Static layout styles for truly page-specific values; all interactive
// elements use the global component classes above.
const styles = {
  headerRow: {
    margin: '0 0 var(--space-4)'
  },
  pageTitle: {
    margin: 0,
    fontSize: '1.4rem',
    fontWeight: 600,
    letterSpacing: '-0.02em'
  },
  messageRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-4)'
  },
  messageText: {
    minWidth: 0,
    overflowWrap: 'anywhere'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'inherit',
    fontSize: '18px',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: 'var(--radius-xs)',
    flexShrink: 0
  },
  sectionSub: {
    marginTop: 'var(--space-1)',
    marginBottom: 'var(--space-3)',
    maxWidth: '64ch'
  },
  saveRow: {
    display: 'flex',
    gap: 'var(--space-2)',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  listCard: {
    marginTop: 'var(--space-5)'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-4)',
    padding: 'var(--space-6) 0'
  },
  loadingText: {
    color: 'var(--text-secondary)',
    fontSize: '0.85rem'
  },
  studyList: {
    marginTop: 'var(--space-3)'
  },
  studyCard: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--space-4)',
    padding: 'var(--space-4)'
  },
  studyInfo: {
    flex: '1 1 320px',
    minWidth: 0
  },
  studyName: {
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    flexWrap: 'wrap'
  },
  studyNameText: {
    minWidth: 0,
    overflowWrap: 'anywhere'
  },
  modalityChips: {
    display: 'inline-flex',
    gap: '6px',
    flexWrap: 'wrap'
  },
  studyDescription: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginTop: '6px',
    overflowWrap: 'anywhere'
  },
  studyMeta: {
    fontSize: '0.8rem',
    color: 'var(--text-tertiary)',
    marginTop: '6px'
  },
  studyContents: {
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
    marginTop: '6px',
    overflowWrap: 'anywhere'
  },
  studyActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
    flexShrink: 0
  },
  confirmModal: {
    width: '100%',
    maxWidth: '440px',
    maxHeight: 'min(84vh, 560px)',
    overflowY: 'auto'
  },
  confirmBody: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    margin: 0
  }
};

export default HistoryPage;
